// Generated by TaroBase SDK Generator: 2026-07-24T09:46:28.100Z
// Collection: rentalDeposits/$depositId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * RENTER-signed onchain transfer of native SOL INTO the treasury (PROJECT_VAULT_ADDRESS) to fund the renter's rentalBalances ledger. isPassthrough — executes the SOL transfer atomically onchain, stays queryable offchain. After the transfer lands, the backend credits the renter's ledger and flips credited=true (idempotent). Native SOL only (lamports).
 */

export interface RentalDepositsRequest {
  renter: AddressType;
  amount: number | TimeOperation | IncrementOperation | TokenAmount;
  credited: boolean;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface RentalDepositsResponse {
  renter: string;
  amount: number;
  credited: boolean;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
  tarobase_transaction_hash?: string | undefined; // Represents the hash of the transaction that created this item. Can be used to create Solscan links (combine with the current network in the Tarobase app)
}

/**
 * Build a RentalDeposits operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildRentalDeposits(depositId: string, data: RentalDepositsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `rentalDeposits/${depositId}`, document: doc };
}

/**
 * Logged-in renter only, and @newData.renter MUST equal @user.address (renters fund their OWN balance). amount must be > 0 and credited must be created as false. The onchain create hook transfers @newData.amount lamports of native SOL FROM the renter (@user.address, who signs) TO the treasury (@constants.PROJECT_VAULT_ADDRESS) via @TokenPlugin.transfer(from, to, mint, amount). amount is in lamports (native SOL, 9 decimals). The backend then flips credited=true after adding amount to the renter's rentalBalances ledger. isPassthrough = executes onchain, record stays queryable offchain. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setRentalDeposits(depositId: string, data: RentalDepositsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`rentalDeposits/${depositId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting RentalDeposits: ${formatError(error)}`);
    return false;
  }
}

export type RentalDepositsRequestUpdate = Partial<RentalDepositsRequest>;

/**
 * Build a RentalDeposits update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateRentalDeposits(depositId: string, data: RentalDepositsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `rentalDeposits/${depositId}`, document: doc };
}

/**
 * BACKEND ONLY. Caller MUST be @constants.PROJECT_VAULT_ADDRESS. The backend sets credited=true once it has added @newData.amount to the renter's rentalBalances ledger, making crediting idempotent. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateRentalDeposits(depositId: string, data: RentalDepositsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`rentalDeposits/${depositId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating RentalDeposits: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read deposit records (renter, amount, credited, createdAt). Publicly readable as required for onchain collections.
   (Get Single Item)
 */
export async function getRentalDeposits(depositId: string): Promise<RentalDepositsResponse | null> {
  try {
    const result = await get(`rentalDeposits/${depositId}`);
    return result as RentalDepositsResponse | null;
  } catch (error) {
    console.error(`Error getting RentalDeposits: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single RentalDeposits document. (
  Read Operation Details: Anyone can read deposit records (renter, amount, credited, createdAt). Publicly readable as required for onchain collections.
  )
 */
export function subscribeRentalDeposits(callback: (data: RentalDepositsResponse | null) => void, depositId: string): Promise<() => Promise<void>> {
  return subscribe(`rentalDeposits/${depositId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as RentalDepositsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to RentalDeposits at path '${`rentalDeposits/${depositId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many RentalDeposits items from collection rentalDeposits
 
  Read Operation Details: Anyone can read deposit records (renter, amount, credited, createdAt). Publicly readable as required for onchain collections.
  
 */
export async function getManyRentalDeposits(filter: string = ""): Promise<RentalDepositsResponse[]> {
  try {
    const results = await get("rentalDeposits", { prompt: filter });
    return Array.isArray(results) ? results as RentalDepositsResponse[] : [];
  } catch (error) {
    console.error(`Error getting RentalDeposits collection at path 'rentalDeposits': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in RentalDeposits collection at rentalDeposits
 
  Read Operation Details: Anyone can read deposit records (renter, amount, credited, createdAt). Publicly readable as required for onchain collections.
  
 */
export function subscribeManyRentalDeposits(callback: (data: RentalDepositsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("rentalDeposits", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as RentalDepositsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to RentalDeposits collection at path '${"rentalDeposits"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all RentalDeposits items from collection rentalDeposits
 
  Read Operation Details: Anyone can read deposit records (renter, amount, credited, createdAt). Publicly readable as required for onchain collections.
  
 */
export async function getAllRentalDeposits(filter: string = ""): Promise<RentalDepositsResponse[]> {
  try {
    const results = await get("rentalDeposits", { prompt: filter });
    return Array.isArray(results) ? results as RentalDepositsResponse[] : [];
  } catch (error) {
    console.error(`Error getting RentalDeposits collection at path 'rentalDeposits': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in RentalDeposits collection at rentalDeposits
 
  Read Operation Details: Anyone can read deposit records (renter, amount, credited, createdAt). Publicly readable as required for onchain collections.
  
 */
export function subscribeAllRentalDeposits(callback: (data: RentalDepositsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("rentalDeposits", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as RentalDepositsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to RentalDeposits collection at path '${"rentalDeposits"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count RentalDeposits items in collection rentalDeposits.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countRentalDeposits(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("rentalDeposits", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting RentalDeposits at path 'rentalDeposits': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on RentalDeposits items in collection rentalDeposits.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateRentalDeposits(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("rentalDeposits", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating RentalDeposits at path 'rentalDeposits': ${formatError(error)}`);
    return { value: 0 };
  }
}

